/* ============================================
   <product-form> Web Component
   Gallery, quantity, subscription, AJAX add-to-cart
   Budget: < 2KB uncompressed
   ============================================ */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-product-form]');
    this.submitBtn = this.querySelector('[data-add-to-cart]');
    this.initGallery();
    this.initVideoSlides();
    this.initUseCaseLinks();
    this.initQuantity();
    this.initSubscription();
    this.initBundles();
    this.initForm();
  }

  /* --- Gallery --- */

  initGallery() {
    this.galleryScroll = this.querySelector('[data-gallery-scroll]');
    if (!this.galleryScroll) return;

    var dots = this.querySelectorAll('[data-gallery-dot]');
    var thumbs = this.querySelectorAll('[data-gallery-thumbnail]');
    var self = this;

    var scrollToSlide = function (index) {
      index = parseInt(index, 10);
      var slides = self.querySelectorAll('[data-gallery-slide]');
      var targetSlide = slides[index];
      if (!targetSlide) return;
      self.galleryScroll.style.scrollSnapType = 'none';
      self.galleryScroll.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: 'smooth',
      });
      setTimeout(function () {
        self.galleryScroll.style.scrollSnapType = '';
      }, 500);
      self.updateGalleryIndicators(index);
    };

    /* --- Auto-play Logic --- */
    var autoplayInterval;
    var autoplayDelay = 4000; // 4 seconds
    var startAutoplay = function () {
      stopAutoplay();
      autoplayInterval = setInterval(function () {
        var slideCount = self.querySelectorAll('[data-gallery-slide]').length;
        if (slideCount <= 1) return;

        var scrollLeft = self.galleryScroll.scrollLeft;
        var slideWidth = self.galleryScroll.offsetWidth;
        var currentIndex = Math.round(scrollLeft / slideWidth);
        var nextIndex = (currentIndex + 1) % slideCount;

        scrollToSlide(nextIndex);
      }, autoplayDelay);
    };

    var stopAutoplay = function () {
      if (autoplayInterval) clearInterval(autoplayInterval);
    };

    /* Start autoplay only on non-touch devices */
    var isTouchDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (!isTouchDevice) {
      startAutoplay();
    }

    /* Pause autoplay on user interaction */
    var galleryContainer = this.galleryScroll.closest('.product-hero__gallery');
    if (galleryContainer) {
      galleryContainer.addEventListener('mouseenter', stopAutoplay);
      galleryContainer.addEventListener('mouseleave', function () {
        if (!isTouchDevice) startAutoplay();
      });
      galleryContainer.addEventListener('touchstart', stopAutoplay, { passive: true });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        stopAutoplay();
        scrollToSlide(dot.dataset.galleryDot);
        startAutoplay();
      });
    });

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        scrollToSlide(thumb.dataset.galleryThumbnail);
      });
    });

    /* Arrow navigation buttons */
    var prevBtn = this.querySelector('[data-gallery-prev]');
    var nextBtn = this.querySelector('[data-gallery-next]');
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        var slideCount = self.querySelectorAll('[data-gallery-slide]').length;
        var scrollLeft = self.galleryScroll.scrollLeft;
        var slideWidth = self.galleryScroll.offsetWidth;
        var currentIndex = Math.round(scrollLeft / slideWidth);
        var prevIndex = (currentIndex - 1 + slideCount) % slideCount;
        stopAutoplay();
        scrollToSlide(prevIndex);
        startAutoplay();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        var slideCount = self.querySelectorAll('[data-gallery-slide]').length;
        var scrollLeft = self.galleryScroll.scrollLeft;
        var slideWidth = self.galleryScroll.offsetWidth;
        var currentIndex = Math.round(scrollLeft / slideWidth);
        var nextIndex = (currentIndex + 1) % slideCount;
        stopAutoplay();
        scrollToSlide(nextIndex);
        startAutoplay();
      });
    }

    /* Sync indicators on scroll-snap settle and restart autoplay */
    var scrollTimer;
    this.galleryScroll.addEventListener(
      'scroll',
      function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var scrollLeft = self.galleryScroll.scrollLeft;
          var slideWidth = self.galleryScroll.offsetWidth;
          var index = Math.round(scrollLeft / slideWidth);
          self.updateGalleryIndicators(index);
          /* Restart the timer after manual scrolling */
          if (!galleryContainer || !galleryContainer.matches(':hover')) {
            startAutoplay();
          }
        }, 100);
      },
      { passive: true }
    );

    /* Keyboard arrow navigation */
    var galleryContainer = this.galleryScroll.closest('.product-hero__gallery');
    if (galleryContainer) {
      galleryContainer.setAttribute('tabindex', '0');
      galleryContainer.setAttribute('role', 'region');
      galleryContainer.setAttribute('aria-label', 'Product image gallery');
      galleryContainer.addEventListener('keydown', function (e) {
        var slideCount = self.querySelectorAll('[data-gallery-slide]').length;
        if (slideCount === 0) return;
        var scrollLeft = self.galleryScroll.scrollLeft;
        var slideWidth = self.galleryScroll.offsetWidth;
        var currentIndex = Math.round(scrollLeft / slideWidth);

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          stopAutoplay();
          scrollToSlide(Math.min(currentIndex + 1, slideCount - 1));
          startAutoplay();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          stopAutoplay();
          scrollToSlide(Math.max(currentIndex - 1, 0));
          startAutoplay();
        }
      });
    }
  }

  /* --- Video Slides --- */

  initVideoSlides() {
    var videos = this.querySelectorAll('[data-video-slide]');
    if (videos.length === 0) return;

    var self = this;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(function () { });
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });

    var toggles = this.querySelectorAll('[data-video-toggle]');
    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var slide = btn.closest('[data-gallery-slide]');
        var video = slide ? slide.querySelector('video') : null;
        if (!video) return;
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });
    });
  }

  /* --- Use Case Badge Links --- */

  initUseCaseLinks() {
    var links = document.querySelectorAll('[data-use-case-link]');
    if (links.length === 0) return;

    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var useCase = link.dataset.useCaseLink;
        var reviewsSection = document.querySelector('[id^="product-reviews"]');
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setTimeout(function () {
            var filterBtn = reviewsSection.querySelector(
              '[data-filter="' + useCase + '"]'
            );
            if (filterBtn) filterBtn.click();
          }, 600);
        }
      });
    });
  }

  updateGalleryIndicators(index) {
    this.querySelectorAll('[data-gallery-dot]').forEach(function (d) {
      d.classList.toggle(
        'product-hero__gallery-dot--active',
        d.dataset.galleryDot == index
      );
    });
    this.querySelectorAll('[data-gallery-thumbnail]').forEach(function (t) {
      t.classList.toggle(
        'product-hero__thumbnail--active',
        t.dataset.galleryThumbnail == index
      );
    });
  }

  /* --- Quantity --- */

  initQuantity() {
    var minus = this.querySelector('[data-quantity-minus]');
    var plus = this.querySelector('[data-quantity-plus]');
    var input = this.querySelector('[data-quantity-input]');

    if (!minus || !plus || !input) return;

    minus.addEventListener('click', function () {
      var val = parseInt(input.value) || 1;
      input.value = Math.max(1, val - 1);
    });

    plus.addEventListener('click', function () {
      var val = parseInt(input.value) || 1;
      input.value = val + 1;
    });
  }

  /* --- Subscription Toggle --- */

  initSubscription() {
    var widget = this.querySelector('[data-subscription-widget]');
    if (!widget) return;

    var radios = widget.querySelectorAll('input[name="purchase_type"]');
    var frequency = widget.querySelector('[data-frequency-selector]');
    var benefits = widget.querySelector('[data-subscription-benefits]');
    var hiddenInput = widget.querySelector('[data-selling-plan-input]');
    var options = widget.querySelectorAll('.subscription-widget__option');
    var planSelect = widget.querySelector('[data-selling-plan-select]');

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        var isSubscription = radio.value === 'subscription';

        options.forEach(function (opt) {
          opt.classList.toggle(
            'subscription-widget__option--active',
            opt.contains(radio)
          );
        });

        if (frequency) {
          frequency.style.opacity = isSubscription ? '1' : '0.4';
          frequency.style.pointerEvents = isSubscription ? '' : 'none';
        }
        if (benefits) {
          benefits.style.opacity = isSubscription ? '1' : '0.4';
        }

        if (hiddenInput) {
          hiddenInput.value = isSubscription
            ? radio.dataset.sellingPlan ||
            (planSelect ? planSelect.value : '')
            : '';
        }
      });
    });

    if (planSelect && hiddenInput) {
      planSelect.addEventListener('change', function () {
        hiddenInput.value = planSelect.value;
      });
    }
  }

  /* --- Bundle Incentives --- */

  initBundles() {
    var input = this.querySelector('[data-quantity-input]');
    var bundleBtns = this.querySelectorAll('[data-bundle-qty]');
    if (!input || bundleBtns.length === 0) return;

    bundleBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var qty = parseInt(btn.dataset.bundleQty);
        input.value = qty;

        /* Visual feedback — highlight selected tier */
        bundleBtns.forEach(function (b) {
          b.classList.toggle('product-hero__bundle-tier--active', b === btn);
        });
      });
    });
  }

  /* --- AJAX Add to Cart --- */

  initForm() {
    if (!this.form) return;
    var self = this;

    var handleSubmit = async function (e) {
      e.preventDefault();
      self.setButtonState('adding');

      try {
        var formData = new FormData(self.form);
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) throw new Error('Add to cart failed');

        var item = await res.json();
        self.setButtonState('added');

        document.dispatchEvent(
          new CustomEvent('cart:item-added', { detail: item })
        );

        setTimeout(function () {
          self.setButtonState('default');
        }, 2000);
      } catch (err) {
        self.setButtonState('error');
        setTimeout(function () {
          self.setButtonState('default');
        }, 3000);
      }
    };

    this.form.addEventListener('submit', handleSubmit);

    if (this.stickyBtn) {
      this.stickyBtn.addEventListener('click', handleSubmit);
    }
  }

  setButtonState(state) {
    var btn = this.submitBtn;
    if (!btn) return;

    if (!btn.dataset.originalText) {
      btn.dataset.originalText = btn.textContent.trim();
    }

    switch (state) {
      case 'adding':
        btn.textContent = btn.dataset.textAdding || 'Adding...';
        btn.disabled = true;
        break;
      case 'added':
        btn.textContent = btn.dataset.textAdded || 'Added \u2713';
        btn.disabled = false;
        btn.classList.add('btn--atc-success');
        setTimeout(function () { btn.classList.remove('btn--atc-success'); }, 400);
        break;
      case 'error':
        btn.textContent = 'Error \u2014 try again';
        btn.disabled = false;
        break;
      default:
        btn.textContent = btn.dataset.originalText;
        btn.disabled = false;
    }
  }

}

customElements.define('product-form', ProductForm);
